// src/components/Footer/Footer.js
import React from 'react';
import ScrollToTopLink from '../ScrollToTopLink/ScrollToTopLink';
import phdscanner from "../../assets/phdscanner_logo.webp";
import { FaLinkedin, FaTwitter, FaFacebook, FaInstagram, FaGraduationCap, FaUniversity, FaEnvelope } from "react-icons/fa";
import { FaLocationDot } from "react-icons/fa6";
import useAnalytics from '../../hooks/useAnalytics';
import { getLoginPath, getDashboardPath, isSaaSSubdomain } from "../../utils/environment";

const Footer = () => {
    const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX';
    const { trackEvent } = useAnalytics(GA_MEASUREMENT_ID);

    const handleLinkClick = (category, label) => {
        trackEvent({
            category: category,
            action: 'Click',
            label: label
        });
    };

    const mainLinks = [
        { text: 'Home', href: '/home' },
        { text: 'PhD Openings', href: '/phd-vacancies/standard' },
        { text: 'Smart Picks', href: '/smart-picks' },
        { text: 'PhD Blogs', href: '/blogs' },
        { text: 'Contact', href: '/contact' },
        { text: 'About Us', href: '/about' },
        // Add SaaS login and dashboard links if on SaaS subdomain
        ...(isSaaSSubdomain() ? [
            { text: 'SaaS Login', href: getLoginPath() },
            { text: 'SaaS Dashboard', href: getDashboardPath() }
        ] : [])
    ];

    const countryLinks = [
        { text: 'Belgium', href: '/phd-vacancies/standard/Belgium' },
        { text: 'Denmark', href: '/phd-vacancies/standard/Denmark' },
        { text: 'Finland', href: '/phd-vacancies/standard/Finland' },
        { text: 'France', href: '/phd-vacancies/standard/France' },
        { text: 'Germany', href: '/phd-vacancies/standard/Germany' },
        { text: 'Luxembourg', href: '/phd-vacancies/standard/Luxembourg' },
        { text: 'Netherlands', href: '/phd-vacancies/standard/Netherlands' },
        { text: 'Norway', href: '/phd-vacancies/standard/Norway' },
        { text: 'Sweden', href: '/phd-vacancies/standard/Sweden' },
        { text: 'Switzerland', href: '/phd-vacancies/standard/Switzerland' },
        { text: 'United Kingdom', href: '/phd-vacancies/standard/United-Kingdom' },
    ];

    const legalLinks = [
        { text: 'Privacy Policy', href: '/privacy-policy' },
        { text: 'Terms of Service', href: '/terms' },
        { text: 'Cookie Policy', href: '/cookies' },
        { text: 'Sitemap', href: '/sitemap' },
    ];

    const socialLinks = [
        { icon: <FaLinkedin />, href: 'https://www.linkedin.com/company/phdscanner', label: 'LinkedIn' },
        { icon: <FaTwitter />, href: 'https://x.com/PhdScanner', label: 'Twitter' },
        { icon: <FaFacebook />, href: 'https://www.facebook.com/Phdscanner', label: 'Facebook' },
        { icon: <FaInstagram />, href: 'https://www.instagram.com/phdscanner', label: 'Instagram' },
    ];

    return (
        <footer className="bg-gradient-to-b from-[#FFEEED] to-[#FFD6D4] text-[#1B222A] pt-20 pb-10 border-t border-[#DB4F40]">
            <div className="container mx-auto px-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10">
                    {/* Brand Column */}
                    <div className="space-y-6">
                        <ScrollToTopLink
                            to="/home"
                            className="flex items-center gap-3"
                            onClick={() => handleLinkClick('Navigation', 'Home')}
                        >
                            <img
                                src={phdscanner}
                                className="h-14 w-auto object-contain"
                                alt="PhDScanner Logo"
                            />
                            <div className="flex flex-col">
                                <span className="text-2xl font-extrabold text-[#2C2C2C] notranslate group-hover:text-[#DB4F40] transition-colors"
                                    style={{ fontFamily: 'Grocetask, sans-serif' }}>
                                    PhDScanner
                                </span>
                                <span className="text-xs text-gray-500 font-medium -mt-1 text-center italic">
                                  <span className="text-[#DB4F40] font-bold">Find</span> Your Calling
                                </span>
                            </div>
                        </ScrollToTopLink>
                        <p className="text-[#1B222A] opacity-80">
                            Empowering researchers and academics with cutting-edge tools for success in their academic journey.
                        </p>
                        <div className="flex space-x-4">
                            {socialLinks.map((social, index) => (
                                <a
                                    key={index}
                                    href={social.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#DB4F40] hover:text-[#c13426] text-xl transition-colors"
                                    onClick={() => handleLinkClick('Social Link', social.label)}
                                >
                                    {social.icon}
                                </a>
                            ))}
                        </div>
                        {/* Contact Info */}
                        <div className="mt-4 space-y-3">
                            <div className="flex items-center gap-3">
                                <FaEnvelope className="text-[#DB4F40]" />
                                <a
                                    href="mailto:admin@phdscanner.com"
                                    className="text-[#1B222A] hover:text-[#DB4F40] transition-colors"
                                    onClick={() => handleLinkClick('Contact', 'Email')}
                                >
                                    admin@phdscanner.com
                                </a>
                            </div>
                            <div className="flex items-start gap-3">
                                <FaLocationDot className="text-[#DB4F40] mt-1" />
                                <address className="not-italic">
                                    <div className="font-semibold text-[#DB4F40]">PhDScanner</div>
                                    <div className="text-[#1B222A]/80">
                                        Richmond Rd, Bradford BD7 1DP<br />
                                        United Kingdom
                                    </div>
                                </address>
                            </div>
                        </div>
                    </div>

                    {/* Main Navigation */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold mb-4 text-[#DB4F40] tracking-wide uppercase">Navigation</h3>
                        <div className="flex flex-col gap-2">
                        {mainLinks.map((link, index) => (
                            <ScrollToTopLink
                                key={index}
                                to={link.href}
                                className="block text-[#1B222A] hover:text-[#DB4F40] transition-colors py-1.5 text-base font-medium"
                                onClick={() => handleLinkClick('Navigation', link.text)}
                            >
                                {link.text}
                            </ScrollToTopLink>
                        ))}
                        </div>
                    </div>

                    {/* Countries - horizontal scroll on mobile */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold mb-4 text-[#DB4F40] tracking-wide uppercase">PhD Openings</h3>
                        <div className="flex flex-col gap-2">
                        {countryLinks.map((link, index) => (
                            <ScrollToTopLink
                                key={index}
                                to={link.href}
                                className="flex items-center gap-2 text-[#1B222A] hover:text-[#DB4F40] transition-colors py-1.5 px-3 rounded-full md:rounded-none md:px-0 text-base font-medium whitespace-nowrap"
                                onClick={() => handleLinkClick('Country Link', link.text)}
                            >
                                <FaGraduationCap className="text-sm" />
                                {link.text}
                            </ScrollToTopLink>
                        ))}
                        </div>
                    </div>

                    {/* Legal */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold mb-4 text-[#DB4F40] tracking-wide uppercase">Legal</h3>
                        <div className="flex flex-col gap-2">
                        {legalLinks.map((link, index) => (
                            <ScrollToTopLink
                                key={index}
                                to={link.href}
                                className="block text-[#1B222A] hover:text-[#DB4F40] transition-colors py-1.5 text-base font-medium"
                                onClick={() => handleLinkClick('Legal Link', link.text)}
                            >
                                {link.text}
                            </ScrollToTopLink>
                        ))}
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div className="border-t border-[#1B222A]/10 mt-12 pt-8 text-center text-[#1B222A] opacity-80">
                    <p>© {new Date().getFullYear()} PhDScanner. All rights reserved.</p>
                    <div className="flex justify-center flex-wrap gap-4 mt-4">
                        {legalLinks.map((link, index) => (
                            <ScrollToTopLink
                                key={index}
                                to={link.href}
                                className="hover:text-[#DB4F40] transition-colors"
                                onClick={() => handleLinkClick('Legal Footer Link', link.text)}
                            >
                                {link.text}
                            </ScrollToTopLink>
                        ))}
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;